/* ---------------------------------------------------------------------------
   Newsletter popup (sections/popup.liquid, styles in assets/te-popup.css)

   Opens once the section's delay has passed, as soon as nothing else has the shopper's attention (menu, cart, search,
   quick view, a field they're typing in). By default it comes back on every page load; the section can instead wait a
   number of days after it was last shown. It never comes back once the shopper has signed up, whether here, in the
   Newsletter signup band or in the footer (remembered in this browser). The form itself is sent by
   assets/te-sections.js (initNewsletter), which shows the thank-you and code.

   On phones the popup is a card in the middle of the screen. While the on-screen keyboard is up it is centred in the
   space above the keyboard with the photo strip folded away, so the field and button stay in view.

   It also opens straight away from links to "#newsletter-popup", and in the theme editor when the section is selected
   (there it never opens by itself). Loaded by the section, after assets/te-sections.js.
   --------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (window.tePopupLoaded) {
    return;
  }

  window.tePopupLoaded = true;

  var STORAGE_KEY = 'te-popup'; // { shown: time last opened, subscribed: true }
  var PENDING_KEY = 'te-popup-pending'; // Set while the form goes the normal way (captcha), read on the page it returns to
  var HASH = '#newsletter-popup';
  var DAY = 24 * 60 * 60 * 1000;
  var RETRY_DELAY = 1500; // Something else is open: try again after this
  var CLOSE_DURATION = 450; // The fade out in assets/te-popup.css
  var PHONE_QUERY = '(max-width: 699px)'; // The phone layout in assets/te-popup.css
  var KEYBOARD_MIN = 80; // Less than this between the screen's bottom and the page's is browser chrome, not a keyboard

  var openTimer = null;
  var hideTimer = null;
  var lastFocus = null;
  var reopenAfterReload = false;
  var stopWatchingKeyboard = null;

  function getPopup() {
    return document.querySelector('[data-te-popup]');
  }

  function isOwnSection(popup, event) {
    return popup && event.detail && event.detail.sectionId === popup.getAttribute('data-section-id');
  }

  /* Memory ------------------------------------------------------------------ */

  function readMemory() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function remember(changes) {
    try {
      var memory = readMemory();

      Object.keys(changes).forEach(function (key) {
        memory[key] = changes[key];
      });

      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
    } catch (error) {
      // Storage blocked: the popup simply shows again on the next visit
    }
  }

  function isDue(popup) {
    var memory = readMemory();
    var days = parseInt(popup.getAttribute('data-reshow-days'), 10); // 0 = every page load, "never" -> NaN

    if (memory.subscribed) {
      return false;
    }

    if (!memory.shown) {
      return true;
    }

    return !isNaN(days) && Date.now() - memory.shown >= days * DAY;
  }

  /* Opening and closing ----------------------------------------------------- */

  // A drawer, the search, another popup (the currency picker is a jQuery-shown .modal), a field being typed in, or a
  // tab in the background
  function isBusy() {
    var html = document.documentElement;
    var active = document.activeElement;
    var jqueryModalOpen = Array.prototype.some.call(document.querySelectorAll('.modal'), function (modal) {
      return modal.getClientRects().length > 0;
    });

    return document.hidden ||
      jqueryModalOpen ||
      html.classList.contains('no-scroll') ||
      html.classList.contains('te-quickview-open') ||
      Boolean(document.querySelector('.Drawer[aria-hidden="false"], .Modal[aria-hidden="false"], .Search[aria-hidden="false"]')) ||
      Boolean(active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName));
  }

  // assets/te-popup.css loads without holding up the page, so wait for it rather than show an unstyled popup
  function whenStyled(callback) {
    var link = document.querySelector('[data-te-popup-css]');

    if (!link || link.media === 'all' || link.sheet) {
      if (link) {
        link.media = 'all';
      }

      callback();
      return;
    }

    link.addEventListener('load', function () {
      link.media = 'all';
      callback();
    }, { once: true });
  }

  // The photo is lazy so it never competes with the page's own first photo; this fetches it ahead of opening
  function loadImage(popup) {
    var image = popup.querySelector('[data-te-popup-image]');

    if (image && image.loading === 'lazy') {
      image.loading = 'eager';
    }
  }

  // The page behind stays still. <html> is padded by the scrollbar's width meanwhile, so nothing shifts sideways.
  function lockScroll() {
    var html = document.documentElement;
    var scrollbar = window.innerWidth - html.clientWidth;

    if (html.classList.contains('te-popup-open')) {
      return;
    }

    if (scrollbar > 0) {
      html.style.paddingRight = scrollbar + 'px';
    }

    html.classList.add('te-popup-open');
  }

  function unlockScroll() {
    var html = document.documentElement;

    html.classList.remove('te-popup-open');
    html.style.paddingRight = '';
  }

  // preview: opened in the theme editor or to show the result of a signup, so it doesn't count as shown
  function open(popup, preview) {
    clearTimeout(openTimer);
    clearTimeout(hideTimer);

    if (popup.classList.contains('is-open')) {
      return;
    }

    whenStyled(function () {
      if (popup !== getPopup() || popup.classList.contains('is-open')) {
        return;
      }

      loadImage(popup);
      lastFocus = document.activeElement;
      lockScroll();

      popup.hidden = false;
      void popup.offsetWidth; // Starts the transition from the closed state
      popup.classList.add('is-open');
      popup.querySelector('[data-te-popup-dialog]').focus({ preventScroll: true });

      document.addEventListener('keydown', onKeydown);
      stopKeyboardWatch();
      stopWatchingKeyboard = watchKeyboard(popup);

      if (!preview) {
        remember({ shown: Date.now() });
      }
    });
  }

  function close(popup) {
    if (!popup || !popup.classList.contains('is-open')) {
      return;
    }

    popup.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);

    clearTimeout(hideTimer);

    hideTimer = setTimeout(function () {
      popup.hidden = true;
      unlockScroll();
      stopKeyboardWatch();
    }, CLOSE_DURATION);

    // A link to #newsletter-popup can open it again
    if (window.location.hash === HASH && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (lastFocus && lastFocus !== document.body && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
      lastFocus.focus({ preventScroll: true });
    }

    lastFocus = null;
  }

  // Keyboard: Escape closes, Tab stays inside the popup
  function onKeydown(event) {
    var popup = getPopup();

    if (!popup || !popup.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape' || event.key === 'Esc') {
      close(popup);
    } else if (event.key === 'Tab') {
      keepFocusInside(popup, event);
    }
  }

  function keepFocusInside(popup, event) {
    var dialog = popup.querySelector('[data-te-popup-dialog]');
    var active = document.activeElement;
    var items = Array.prototype.filter.call(
      dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (item) {
        return item.getClientRects().length > 0;
      }
    );

    if (!items.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Phones ------------------------------------------------------------------ */

  function isPhone() {
    return window.matchMedia(PHONE_QUERY).matches;
  }

  // The keyboard covers the bottom of the page, and browsers only shrink the visible area (the visual viewport). The
  // popup is padded to that area, so the card is centred above the keyboard and never reaches above the top of the
  // screen; the photo strip folds away (is-typing) to leave room for the form.
  function watchKeyboard(popup) {
    var viewport = window.visualViewport;

    if (!viewport) {
      return function () {};
    }

    var update = function () {
      var hidden = Math.round(window.innerHeight - viewport.height - viewport.offsetTop);
      var typing = hidden > KEYBOARD_MIN && viewport.scale < 1.01 && isPhone(); // Zooming in also shrinks the visible area
      var active = document.activeElement;

      popup.style.setProperty('--te-popup-keyboard', (typing ? hidden : 0) + 'px');
      popup.style.setProperty('--te-popup-top', (typing ? Math.max(0, Math.round(viewport.offsetTop)) : 0) + 'px');
      popup.classList.toggle('is-typing', typing);

      if (typing && active && popup.contains(active) && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest' });
      }
    };

    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    update();

    return function () {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      popup.style.removeProperty('--te-popup-keyboard');
      popup.style.removeProperty('--te-popup-top');
      popup.classList.remove('is-typing');
    };
  }

  function stopKeyboardWatch() {
    if (stopWatchingKeyboard) {
      stopWatchingKeyboard();
      stopWatchingKeyboard = null;
    }
  }

  /* Start ------------------------------------------------------------------- */

  function schedule(popup) {
    var delay = Math.max(0, parseFloat(popup.getAttribute('data-delay')) || 0) * 1000;

    clearTimeout(openTimer);

    openTimer = setTimeout(function attempt() {
      // Gone (editor), already open, or signed up / shown in another tab meanwhile
      if (popup !== getPopup() || popup.classList.contains('is-open') || !isDue(popup)) {
        return;
      }

      if (isBusy()) {
        openTimer = setTimeout(attempt, RETRY_DELAY);
      } else {
        open(popup, false);
      }
    }, delay);

    // Fetch the photo once the page itself has finished loading, so it's there when the popup opens
    if (document.readyState === 'complete') {
      loadImage(popup);
    } else {
      window.addEventListener('load', function () {
        loadImage(popup);
      }, { once: true });
    }
  }

  // Back from a signup sent the normal way (Shopify's captcha): show its result (the thank-you, or Shopify's error)
  function openAfterCaptcha(popup) {
    var pending = false;

    try {
      pending = window.sessionStorage.getItem(PENDING_KEY) === '1';
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch (error) {
      // Storage blocked
    }

    if (/[?&]customer_posted=true/.test(window.location.search)) {
      remember({ subscribed: true });
    }

    if (pending) {
      open(popup, true);
    }

    return pending;
  }

  function init() {
    var popup = getPopup();

    if (!popup || popup.tePopup) {
      return;
    }

    popup.tePopup = true;

    // Shopify drew the thank-you itself (after a captcha, or the editor's preview)
    if (popup.querySelector('.te-popup__form [data-te-newsletter-success]')) {
      popup.classList.add('is-subscribed');
    }

    if (popup.hasAttribute('data-editor')) {
      // A changed setting re-draws the section: keep it open if it was
      if (reopenAfterReload) {
        reopenAfterReload = false;
        open(popup, true);
      }

      return;
    }

    if (openAfterCaptcha(popup)) {
      return;
    }

    if (window.location.hash === HASH) {
      open(popup, false);
    } else if (isDue(popup)) {
      schedule(popup);
    }
  }

  document.addEventListener('click', function (event) {
    var closer = event.target.closest && event.target.closest('[data-te-popup-close]');

    if (closer) {
      close(closer.closest('[data-te-popup]'));
    }
  });

  window.addEventListener('hashchange', function () {
    var popup = getPopup();

    if (popup && window.location.hash === HASH) {
      open(popup, false);
    }
  });

  // Signed up in any newsletter form (assets/te-sections.js): never ask again. In the popup the thank-you and the code
  // take the place of the intro.
  document.addEventListener('te:newsletter:subscribed', function (event) {
    var popup = getPopup();

    remember({ subscribed: true });
    clearTimeout(openTimer);

    if (popup && event.target === popup) {
      popup.classList.add('is-subscribed');
    }
  });

  // When te-sections.js has to send the popup's form the normal way (it removes data-nocaptcha first), the page reloads:
  // note it, so the popup opens again there with the result. Capture phase, before anything can stop the event.
  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (form.classList && form.classList.contains('te-popup__form') && !form.hasAttribute('data-nocaptcha')) {
      try {
        window.sessionStorage.setItem(PENDING_KEY, '1');
      } catch (error) {
        // Storage blocked: the shopper lands back on the page without the popup
      }
    }
  }, true);

  // Theme editor
  document.addEventListener('shopify:section:select', function (event) {
    var popup = getPopup();

    if (isOwnSection(popup, event)) {
      open(popup, true);
    }
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var popup = getPopup();

    if (isOwnSection(popup, event)) {
      close(popup);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var popup = getPopup();

    if (isOwnSection(popup, event)) {
      reopenAfterReload = popup.classList.contains('is-open');
      clearTimeout(openTimer);
      clearTimeout(hideTimer);
      document.removeEventListener('keydown', onKeydown);
      unlockScroll();
      stopKeyboardWatch();
    }
  });

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
